import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
  FiLoader,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiUsers,
  FiHome,
  FiMapPin,
  FiBookOpen,
  FiArrowLeft,
  FiArrowRight,
  FiFilter
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertTriangle, X } from "lucide-react";
import api from "../../../connector/api";
import CustomSelect from "../reuse/CustomSelect";
import SchoolYearFilterDropdown from "../reuse/SchoolYearFilterDropdown";

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 50, 100];
const GRADE_ORDER = [
  "pre-school",
  "preschool",
  "nursery",
  "kindergarten",
  "k1",
  "k2",
  "grade 1",
  "grade 2",
  "grade 3",
  "grade 4",
  "grade 5",
  "grade 6",
  "grade 7",
  "grade 8",
  "grade 9",
  "grade 10",
  "grade 11",
  "grade 12"
];

// Toast Item Component
const ToastNotification = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, 3500);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg border text-xs font-semibold max-w-sm pointer-events-auto ${
        toast.type === "error"
          ? "bg-rose-50 border-rose-200 text-rose-800"
          : "bg-emerald-50 border-emerald-200 text-emerald-800"
      }`}
    >
      {toast.type === "error" ? (
        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
      ) : (
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
      )}
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={() => onClose(toast.id)}
        className="p-0.5 rounded text-zinc-400 hover:text-zinc-600 cursor-pointer"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
};

export default function ClassroomTab() {
  // Navigation State
  const [viewMode, setViewMode] = useState("list"); // "list" | "detail"

  // Primary Master State
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("ALL");

  // Master Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false);

  // Form / Dropdown Options
  const [sections, setSections] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [teachers, setTeachers] = useState([]);

  // Active Selected Detail Classroom
  const [activeClassroom, setActiveClassroom] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Transfer State for Detail Page
  const [allStudents, setAllStudents] = useState([]);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [selectedAvailableStudents, setSelectedAvailableStudents] = useState([]);
  const [selectedAssignedStudents, setSelectedAssignedStudents] = useState([]);

  const [allSubjects, setAllSubjects] = useState([]);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedAvailableSubjects, setSelectedAvailableSubjects] = useState([]);
  const [selectedAssignedSubjects, setSelectedAssignedSubjects] = useState([]);

  // Search filters inside detail transfer boxes
  const [studentSearchAvailable, setStudentSearchAvailable] = useState("");
  const [studentSearchAssigned, setStudentSearchAssigned] = useState("");
  const [subjectSearchAvailable, setSubjectSearchAvailable] = useState("");
  const [subjectSearchAssigned, setSubjectSearchAssigned] = useState("");

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  // Toast Notification State
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const [formData, setFormData] = useState({
    section_id: "",
    room_id: "",
    school_year_id: "",
    homeroom_teacher_id: ""
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Dropdown States
  const [openDropdown, setOpenDropdown] = useState(null);
  const modalDropdownRef = useRef(null);
  const pageSizeRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalDropdownRef.current && !modalDropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Form Dropdown Options
  const fetchFormOptions = async () => {
    try {
      const [secRes, rmRes, syRes, teachRes, studRes, subRes] = await Promise.allSettled([
        api.get("/academic/class-section/", { params: { page_size: 1000 } }),
        api.get("/rooms/", { params: { page_size: 1000 } }),
        api.get("/academic/years/", { params: { page_size: 1000 } }),
        api.get("/employees/", { params: { page_size: 1000 } }),
        api.get("/students/", { params: { page_size: 1000 } }),
        api.get("/academic/employee-subjects/", { params: { page_size: 1000 } }),
      ]);

      if (secRes.status === "fulfilled") {
        const data = secRes.value.data;
        setSections(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (rmRes.status === "fulfilled") {
        const data = rmRes.value.data;
        setRooms(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (syRes.status === "fulfilled") {
        const data = syRes.value.data;
        setSchoolYears(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (teachRes.status === "fulfilled") {
        const data = teachRes.value.data;
        setTeachers(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (studRes.status === "fulfilled") {
        const data = studRes.value.data;
        setAllStudents(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
      if (subRes.status === "fulfilled") {
        const data = subRes.value.data;
        setAllSubjects(Array.isArray(data.results) ? data.results : Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to load options:", err);
    }
  };

  // Fetch Master Classrooms List
  const fetchClassrooms = async () => {
    setLoading(true);
    try {
      const res = await api.get("/academic/classroom/", {
        params: {
          page: currentPage,
          page_size: pageSize,
          search: searchQuery || undefined,
          school_year: selectedSchoolYear !== "ALL" ? selectedSchoolYear : undefined
        }
      });

      if (res.data && Array.isArray(res.data.results)) {
        setClassrooms(res.data.results);
        setTotalCount(res.data.count || 0);
      } else if (Array.isArray(res.data)) {
        setClassrooms(res.data);
        setTotalCount(res.data.length);
      }
    } catch (err) {
      console.error("Failed to fetch classrooms:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassrooms();
  }, [currentPage, pageSize, searchQuery, selectedSchoolYear]);

  useEffect(() => {
    fetchFormOptions();
  }, []);

  const getGradeRank = (name = "") => {
    const lowerName = name.toLowerCase();
    for (let i = 0; i < GRADE_ORDER.length; i++) {
      if (lowerName.includes(GRADE_ORDER[i])) return i;
    }
    const numMatch = lowerName.match(/\b(?:grade|g|year)?\s*(\d+)\b/);
    if (numMatch) return 100 + parseInt(numMatch[1], 10);

    return 999;
  };

  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const nameA = a.name || a.section_name || "";
    const nameB = b.name || b.section_name || "";
    const rankA = getGradeRank(nameA);
    const rankB = getGradeRank(nameB);

    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
  });

  // Fetch Full Detail Data for a Classroom
  const openClassroomDetail = async (cls) => {
    setDetailLoading(true);
    setViewMode("detail");
    try {
      const res = await api.get(`/academic/classroom/${cls.id}/`);
      const detailData = res.data;
      setActiveClassroom(detailData);

      const assignedStuds = Array.isArray(detailData.students) ? detailData.students : [];
      const assignedSubs = Array.isArray(detailData.subjects) ? detailData.subjects : [];

      setAssignedStudents(assignedStuds);
      setAssignedSubjects(assignedSubs);

      setSelectedAvailableStudents([]);
      setSelectedAssignedStudents([]);
      setSelectedAvailableSubjects([]);
      setSelectedAssignedSubjects([]);
    } catch (err) {
      console.error("Error loading classroom details:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open / Close Creation Modal
  const handleOpenModal = (item = null) => {
    setFormError("");
    setOpenDropdown(null);
    if (item) {
      setEditingItem(item);
      setFormData({
        section_id: item.section?.id || item.section_id || "",
        room_id: item.room?.id || item.room_id || "",
        school_year_id: item.school_year?.id || item.school_year_id || "",
        homeroom_teacher_id: item.homeroom_teacher?.id || item.homeroom_teacher_id || ""
      });
    } else {
      setEditingItem(null);
      setFormData({
        section_id: "",
        room_id: "",
        school_year_id: schoolYears.find((y) => y.is_active)?.id || schoolYears[0]?.id || "",
        homeroom_teacher_id: ""
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setOpenDropdown(null);
    setFormData({
      section_id: "",
      room_id: "",
      school_year_id: "",
      homeroom_teacher_id: ""
    });
    setFormError("");
  };

  // Submit Modal Action
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);

    try {
      const payload = {
        section_id: formData.section_id ? Number(formData.section_id) : null,
        room_id: formData.room_id ? Number(formData.room_id) : null,
        school_year_id: formData.school_year_id ? Number(formData.school_year_id) : null,
        homeroom_teacher_id: formData.homeroom_teacher_id ? Number(formData.homeroom_teacher_id) : null
      };

      let response;
      if (editingItem) {
        response = await api.put(`/academic/classroom/${editingItem.id}/`, payload);
        if (activeClassroom && activeClassroom.id === editingItem.id) {
          setActiveClassroom((prev) => ({ ...prev, ...response.data }));
        }
        showToast("Classroom details updated successfully!");
      } else {
        response = await api.post("/academic/classroom/", payload);
        showToast("Classroom created successfully!");
      }

      handleCloseModal();
      fetchClassrooms();

      if (!editingItem && response?.data?.id) {
        openClassroomDetail(response.data);
      }
    } catch (err) {
      console.error("Error saving classroom:", err);
      const msg = err.response?.data?.detail || "Failed to save classroom settings.";
      setFormError(msg);
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Action
  const handleDelete = async () => {
    if (!deletingItem) return;
    setSubmitting(true);
    try {
      await api.delete(`/academic/classroom/${deletingItem.id}/`);
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
      if (viewMode === "detail" && activeClassroom?.id === deletingItem.id) {
        setViewMode("list");
        setActiveClassroom(null);
      }
      fetchClassrooms();
      showToast("Classroom section deleted successfully.");
    } catch (err) {
      console.error("Error deleting classroom:", err);
      showToast("Failed to delete classroom.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Save Transfer Changes for Students & Subjects in Detail View
  const handleSyncClassroomAssignments = async (newStuds, newSubs) => {
    if (!activeClassroom) return;
    try {
      const payload = {
        student_ids: newStuds.map((s) => s.id),
        subject_ids: newSubs.map((s) => s.id)
      };

      await api.patch(`/academic/classroom/${activeClassroom.id}/`, payload);
      fetchClassrooms();
      showToast("Assignments updated successfully!");
    } catch (err) {
      console.error("Failed to sync classroom assignments:", err);
      showToast("Failed to sync assignments.", "error");
    }
  };

  // Transfer Control Handlers - Students
  const moveStudentsToAssigned = () => {
    const toMove = allStudents.filter(
      (s) => selectedAvailableStudents.includes(s.id) && !assignedStudents.some((a) => a.id === s.id)
    );
    const updated = [...assignedStudents, ...toMove];
    setAssignedStudents(updated);
    setSelectedAvailableStudents([]);
    handleSyncClassroomAssignments(updated, assignedSubjects);
  };

  const moveStudentsToAvailable = () => {
    const updated = assignedStudents.filter((s) => !selectedAssignedStudents.includes(s.id));
    setAssignedStudents(updated);
    setSelectedAssignedStudents([]);
    handleSyncClassroomAssignments(updated, assignedSubjects);
  };

  // Transfer Control Handlers - Subjects
  const moveSubjectsToAssigned = () => {
    const toMove = allSubjects.filter(
      (s) => selectedAvailableSubjects.includes(s.id) && !assignedSubjects.some((a) => a.id === s.id)
    );
    const updated = [...assignedSubjects, ...toMove];
    setAssignedSubjects(updated);
    setSelectedAvailableSubjects([]);
    handleSyncClassroomAssignments(assignedStudents, updated);
  };

  const moveSubjectsToAvailable = () => {
    const updated = assignedSubjects.filter((s) => !selectedAssignedSubjects.includes(s.id));
    setAssignedSubjects(updated);
    setSelectedAssignedSubjects([]);
    handleSyncClassroomAssignments(assignedStudents, updated);
  };

  // Filtering list items in dual boxes
  const availableStudentsList = allStudents.filter(
    (s) =>
      !assignedStudents.some((a) => a.id === s.id) &&
      (s.full_name || `${s.first_name || ""} ${s.last_name || ""}`)
        .toLowerCase()
        .includes(studentSearchAvailable.toLowerCase())
  );

  const assignedStudentsList = assignedStudents.filter((s) =>
    (s.full_name || `${s.first_name || ""} ${s.last_name || ""}`)
      .toLowerCase()
      .includes(studentSearchAssigned.toLowerCase())
  );

  const availableSubjectsList = allSubjects.filter(
    (s) =>
      !assignedSubjects.some((a) => a.id === s.id) &&
      (s.display_name || s.subject_name || `Subject #${s.id}`)
        .toLowerCase()
        .includes(subjectSearchAvailable.toLowerCase())
  );

  const assignedSubjectsList = assignedSubjects.filter((s) =>
    (s.display_name || s.subject_name || `Subject #${s.id}`)
      .toLowerCase()
      .includes(subjectSearchAssigned.toLowerCase())
  );

  // Pagination Helper Calculations
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="space-y-4">
      {/* Floating Toast Notification Container */}
      {toasts.length > 0 &&
        createPortal(
          <div className="fixed top-5 right-5 z-[10000] flex flex-col gap-2">
            {toasts.map((toast) => (
              <ToastNotification key={toast.id} toast={toast} onClose={removeToast} />
            ))}
          </div>,
          document.body
        )}

      {/* =========================================================================
          VIEW 1: MASTER CLASSROOM LIST
      ========================================================================= */}
      {viewMode === "list" && (
        <>
          {/* Top Filter and Search Bar */}
          <div className="flex flex-col font-dynamic-title sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-64">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search classroom..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                />
              </div>
              <SchoolYearFilterDropdown
                selectedSchoolYear={selectedSchoolYear}
                setSelectedSchoolYear={setSelectedSchoolYear}
                schoolYears={schoolYears}
                setCurrentPage={setCurrentPage}
              />
            </div>

            <button
              onClick={() => handleOpenModal()}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 active:bg-zinc-950 transition-colors shrink-0"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Create Classroom</span>
            </button>
          </div>

          {/* Master Classroom Table */}
          <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white shadow-xs">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 text-zinc-400 space-y-2">
                <FiLoader className="w-6 h-6 animate-spin text-zinc-500" />
                <p className="text-xs font-medium">Loading classrooms...</p>
              </div>
            ) : sortedClassrooms.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
                        <th className="px-4 py-3">Classroom Display Name</th>
                        <th className="px-4 py-3">Room</th>
                        <th className="px-4 py-3">School Year</th>
                        <th className="px-4 py-3">Homeroom Teacher</th>
                        <th className="px-4 py-3">Students</th>
                        <th className="px-4 py-3">Subjects</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 text-zinc-700">
                      {sortedClassrooms.map((cls) => (
                        <tr
                          key={cls.id}
                          onClick={() => openClassroomDetail(cls)}
                          className="hover:bg-zinc-50/80 cursor-pointer transition-colors group"
                        >
                          <td className="px-4 py-3 font-semibold text-zinc-900 group-hover:text-black">
                            <div className="flex items-center gap-2">
                              <FiHome className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-600 shrink-0" />
                              <span>{cls.name || cls.section_name || `Classroom #${cls.id}`}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-zinc-600">
                            {cls.room_code ? (
                              <span className="flex items-center gap-1">
                                <FiMapPin className="w-3 h-3 text-zinc-400" />
                                {cls.room_code}
                              </span>
                            ) : (
                              <span className="text-zinc-300">Unassigned</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-zinc-600">{cls.school_year_name || "-"}</td>
                          <td className="px-4 py-3 text-zinc-600">
                            {cls.teacher_name || <span className="text-zinc-300">Unassigned</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200/80 rounded-full text-[11px] text-zinc-700 font-semibold">
                              {cls.student_count ?? cls.students?.length ?? 0} Student(s)
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200/80 rounded-full text-[11px] text-zinc-700 font-semibold">
                              {cls.subject_count ?? cls.subjects?.length ?? 0} Subject(s)
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="px-4 py-3 border-t border-zinc-200/80 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
                  <div className="flex items-center gap-2">
                    <span>Show</span>
                    <div className="relative" ref={pageSizeRef}>
                      <button
                        type="button"
                        onClick={() => setIsPageSizeDropdownOpen(!isPageSizeDropdownOpen)}
                        className="flex items-center justify-between gap-2 px-2.5 py-1 bg-white border border-zinc-200/80 rounded-lg text-xs font-semibold text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors"
                      >
                        <span>{pageSize}</span>
                        <FiChevronDown
                          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                            isPageSizeDropdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {isPageSizeDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-20"
                            onClick={() => setIsPageSizeDropdownOpen(false)}
                          />
                          <div className="absolute bottom-full mb-1 left-0 w-20 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-30 max-h-48 overflow-y-auto">
                            {PAGE_SIZE_OPTIONS.map((size) => (
                              <button
                                key={size}
                                onClick={() => {
                                  setPageSize(size);
                                  setCurrentPage(1);
                                  setIsPageSizeDropdownOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                                  pageSize === size
                                    ? "font-bold text-zinc-900 bg-zinc-50"
                                    : "text-zinc-600"
                                }`}
                              >
                                <span>{size}</span>
                                {pageSize === size && <FiCheck className="w-3 h-3 text-zinc-700" />}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    <span>entries</span>
                    <span className="text-zinc-300 mx-1">|</span>
                    <span>
                      Showing <strong className="text-zinc-800">{startIndex}</strong> to{" "}
                      <strong className="text-zinc-800">{endIndex}</strong> of{" "}
                      <strong className="text-zinc-800">{totalCount}</strong> entries
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 transition-colors"
                    >
                      <FiChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                          currentPage === page
                            ? "bg-zinc-900 text-white"
                            : "bg-white border border-zinc-200/80 text-zinc-600 hover:bg-zinc-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 transition-colors"
                    >
                      <FiChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-400 space-y-2">
                <FiHome className="w-8 h-8 text-zinc-300" />
                <p className="text-xs font-semibold text-zinc-700">No Classrooms Found</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* =========================================================================
          VIEW 2: DEDICATED CLASSROOM DETAIL PAGE
      ========================================================================= */}
      {viewMode === "detail" && activeClassroom && (
        <div className="space-y-5 animate-in fade-in duration-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 border border-zinc-200/80 rounded-lg shadow-xs">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setViewMode("list")}
                className="p-2 border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-600 transition-colors"
                title="Back to Classrooms List"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
                  <span>{activeClassroom.display_name || activeClassroom.section?.name || `Classroom #${activeClassroom.id}`}</span>
                </h2>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                  <span>Section: <strong className="text-zinc-700">{activeClassroom.section?.name || "N/A"}</strong></span>
                  <span>•</span>
                  <span>Room: <strong className="text-zinc-700">{activeClassroom.room?.code || "Unassigned"}</strong></span>
                  <span>•</span>
                  <span>School Year: <strong className="text-zinc-700">{activeClassroom.school_year?.name || "N/A"}</strong></span>
                  <span>•</span>
                  <span>Homeroom Teacher: <strong className="text-zinc-700">{activeClassroom.homeroom_teacher?.full_name || "Unassigned"}</strong></span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenModal(activeClassroom)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 text-zinc-700 rounded-lg text-xs font-semibold hover:bg-zinc-50 transition-colors"
              >
                <FiEdit2 className="w-3.5 h-3.5" />
                <span>Edit Details</span>
              </button>
              <button
                onClick={() => {
                  setDeletingItem(activeClassroom);
                  setIsDeleteModalOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 bg-red-50/50 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors"
              >
                <FiTrash2 className="w-3.5 h-3.5" />
                <span>Delete</span>
              </button>
            </div>
          </div>

          {detailLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-zinc-400 space-y-2">
              <FiLoader className="w-6 h-6 animate-spin text-zinc-500" />
              <p className="text-xs font-medium">Loading classroom transfers...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* STUDENTS DUAL TRANSFER BOX */}
              <div className="bg-white border border-zinc-200/80 rounded-lg p-4 shadow-xs space-y-3">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                  <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-2">
                    <FiUsers className="w-4 h-4 text-zinc-500" />
                    <span>Manage Students</span>
                  </h3>
                  <span className="text-xs text-zinc-500">
                    Total Assigned: <strong className="text-zinc-900">{assignedStudents.length}</strong>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-11 gap-3 items-center">
                  <div className="md:col-span-5 border border-zinc-200/80 rounded-lg p-3 bg-zinc-50/30 flex flex-col h-64">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-zinc-700">Available Students</span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        {availableStudentsList.length} items
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Filter available..."
                      value={studentSearchAvailable}
                      onChange={(e) => setStudentSearchAvailable(e.target.value)}
                      className="w-full mb-2 px-2.5 py-1 bg-white border border-zinc-200 rounded text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                    />
                    <div className="flex-1 overflow-y-auto space-y-1 bg-white border border-zinc-200/60 rounded p-1.5">
                      {availableStudentsList.length > 0 ? (
                        availableStudentsList.map((st) => {
                          const selected = selectedAvailableStudents.includes(st.id);
                          return (
                            <div
                              key={st.id}
                              onClick={() => {
                                setSelectedAvailableStudents((prev) =>
                                  selected ? prev.filter((id) => id !== st.id) : [...prev, st.id]
                                );
                              }}
                              className={`p-1.5 rounded cursor-pointer text-xs flex items-center justify-between transition-colors ${
                                selected ? "bg-zinc-800 text-white font-semibold" : "hover:bg-zinc-100 text-zinc-700"
                              }`}
                            >
                              <span>{st.full_name || `${st.first_name || ""} ${st.last_name || ""}`.trim()}</span>
                              <span className={`text-[10px] ${selected ? "text-zinc-300" : "text-zinc-400"}`}>
                                #{st.student_number || st.id}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-[11px] text-zinc-400 text-center py-6">No available students</p>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-1 flex md:flex-col items-center justify-center gap-2 py-2">
                    <button
                      onClick={moveStudentsToAssigned}
                      disabled={selectedAvailableStudents.length === 0}
                      className="p-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-30 transition-colors shadow-xs"
                      title="Add Selected"
                    >
                      <FiArrowRight className="w-4 h-4 hidden md:block" />
                      <FiChevronRight className="w-4 h-4 md:hidden" />
                    </button>
                    <button
                      onClick={moveStudentsToAvailable}
                      disabled={selectedAssignedStudents.length === 0}
                      className="p-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-30 transition-colors shadow-xs"
                      title="Remove Selected"
                    >
                      <FiArrowLeft className="w-4 h-4 hidden md:block" />
                      <FiChevronLeft className="w-4 h-4 md:hidden" />
                    </button>
                  </div>

                  <div className="md:col-span-5 border border-zinc-200/80 rounded-lg p-3 bg-zinc-50/30 flex flex-col h-64">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-zinc-700">In This Classroom</span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        {assignedStudentsList.length} items
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Filter assigned..."
                      value={studentSearchAssigned}
                      onChange={(e) => setStudentSearchAssigned(e.target.value)}
                      className="w-full mb-2 px-2.5 py-1 bg-white border border-zinc-200 rounded text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                    />
                    <div className="flex-1 overflow-y-auto space-y-1 bg-white border border-zinc-200/60 rounded p-1.5">
                      {assignedStudentsList.length > 0 ? (
                        assignedStudentsList.map((st) => {
                          const selected = selectedAssignedStudents.includes(st.id);
                          return (
                            <div
                              key={st.id}
                              onClick={() => {
                                setSelectedAssignedStudents((prev) =>
                                  selected ? prev.filter((id) => id !== st.id) : [...prev, st.id]
                                );
                              }}
                              className={`p-1.5 rounded cursor-pointer text-xs flex items-center justify-between transition-colors ${
                                selected ? "bg-zinc-800 text-white font-semibold" : "hover:bg-zinc-100 text-zinc-700"
                              }`}
                            >
                              <span>{st.full_name || `${st.first_name || ""} ${st.last_name || ""}`.trim()}</span>
                              <span className={`text-[10px] ${selected ? "text-zinc-300" : "text-zinc-400"}`}>
                                #{st.student_number || st.id}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-[11px] text-zinc-400 text-center py-6">No students assigned</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* SUBJECTS DUAL TRANSFER BOX */}
              <div className="bg-white border border-zinc-200/80 rounded-lg p-4 shadow-xs space-y-3">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                  <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-2">
                    <FiBookOpen className="w-4 h-4 text-zinc-500" />
                    <span>Manage Subjects</span>
                  </h3>
                  <span className="text-xs text-zinc-500">
                    Total Assigned: <strong className="text-zinc-900">{assignedSubjects.length}</strong>
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-11 gap-3 items-center">
                  <div className="md:col-span-5 border border-zinc-200/80 rounded-lg p-3 bg-zinc-50/30 flex flex-col h-64">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-zinc-700">Available Subject Assignments</span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        {availableSubjectsList.length} items
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Filter available..."
                      value={subjectSearchAvailable}
                      onChange={(e) => setSubjectSearchAvailable(e.target.value)}
                      className="w-full mb-2 px-2.5 py-1 bg-white border border-zinc-200 rounded text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                    />
                    <div className="flex-1 overflow-y-auto space-y-1 bg-white border border-zinc-200/60 rounded p-1.5">
                      {availableSubjectsList.length > 0 ? (
                        availableSubjectsList.map((sub) => {
                          const selected = selectedAvailableSubjects.includes(sub.id);
                          return (
                            <div
                              key={sub.id}
                              onClick={() => {
                                setSelectedAvailableSubjects((prev) =>
                                  selected ? prev.filter((id) => id !== sub.id) : [...prev, sub.id]
                                );
                              }}
                              className={`p-1.5 rounded cursor-pointer text-xs flex items-center justify-between transition-colors ${
                                selected ? "bg-zinc-800 text-white font-semibold" : "hover:bg-zinc-100 text-zinc-700"
                              }`}
                            >
                              <span>{sub.display_name || sub.subject_name || `Subject #${sub.id}`}</span>
                              <span className={`text-[10px] ${selected ? "text-zinc-300" : "text-zinc-400"}`}>
                                #{sub.id}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-[11px] text-zinc-400 text-center py-6">No available subjects</p>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-1 flex md:flex-col items-center justify-center gap-2 py-2">
                    <button
                      onClick={moveSubjectsToAssigned}
                      disabled={selectedAvailableSubjects.length === 0}
                      className="p-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-30 transition-colors shadow-xs"
                      title="Add Selected"
                    >
                      <FiArrowRight className="w-4 h-4 hidden md:block" />
                      <FiChevronRight className="w-4 h-4 md:hidden" />
                    </button>
                    <button
                      onClick={moveSubjectsToAvailable}
                      disabled={selectedAssignedSubjects.length === 0}
                      className="p-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-30 transition-colors shadow-xs"
                      title="Remove Selected"
                    >
                      <FiArrowLeft className="w-4 h-4 hidden md:block" />
                      <FiChevronLeft className="w-4 h-4 md:hidden" />
                    </button>
                  </div>

                  <div className="md:col-span-5 border border-zinc-200/80 rounded-lg p-3 bg-zinc-50/30 flex flex-col h-64">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-zinc-700">In This Classroom</span>
                      <span className="text-[10px] text-zinc-400 font-medium">
                        {assignedSubjectsList.length} items
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder="Filter assigned..."
                      value={subjectSearchAssigned}
                      onChange={(e) => setSubjectSearchAssigned(e.target.value)}
                      className="w-full mb-2 px-2.5 py-1 bg-white border border-zinc-200 rounded text-xs text-zinc-800 focus:outline-none focus:border-zinc-400"
                    />
                    <div className="flex-1 overflow-y-auto space-y-1 bg-white border border-zinc-200/60 rounded p-1.5">
                      {assignedSubjectsList.length > 0 ? (
                        assignedSubjectsList.map((sub) => {
                          const selected = selectedAssignedSubjects.includes(sub.id);
                          return (
                            <div
                              key={sub.id}
                              onClick={() => {
                                setSelectedAssignedSubjects((prev) =>
                                  selected ? prev.filter((id) => id !== sub.id) : [...prev, sub.id]
                                );
                              }}
                              className={`p-1.5 rounded cursor-pointer text-xs flex items-center justify-between transition-colors ${
                                selected ? "bg-zinc-800 text-white font-semibold" : "hover:bg-zinc-100 text-zinc-700"
                              }`}
                            >
                              <span>{sub.display_name || sub.subject_name || `Subject #${sub.id}`}</span>
                              <span className={`text-[10px] ${selected ? "text-zinc-300" : "text-zinc-400"}`}>
                                #{sub.id}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-[11px] text-zinc-400 text-center py-6">No subjects assigned</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* =========================================================================
          SIMPLIFIED CREATE / EDIT CLASSROOM MODAL
      ========================================================================= */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-lg overflow-visible animate-in fade-in zoom-in-95 duration-150">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50 rounded-t-lg">
                <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                  {editingItem ? "Edit Classroom Settings" : "Create New Classroom"}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-600">
                    <FiAlertCircle className="w-4 h-4 shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <CustomSelect
                    label="Section"
                    placeholder="Select Section..."
                    value={formData.section_id || ""}
                    options={sections}
                    onChange={(val) => setFormData({ ...formData, section_id: val })}
                    getOptionLabel={(sec) => sec.name}
                    getOptionValue={(sec) => sec.id}
                  />

                  <CustomSelect
                    label="Room"
                    placeholder="Select Room..."
                    value={formData.room_id || ""}
                    options={rooms}
                    onChange={(val) => setFormData({ ...formData, room_id: val })}
                    getOptionLabel={(rm) => rm.code}
                    getOptionValue={(rm) => rm.id}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <CustomSelect
                    label="School Year"
                    placeholder="Select School Year..."
                    value={formData.school_year_id || ""}
                    options={schoolYears}
                    onChange={(val) => setFormData({ ...formData, school_year_id: val })}
                    getOptionLabel={(sy) => sy.name}
                    getOptionValue={(sy) => sy.id}
                  />

                  <CustomSelect
                    label="Homeroom Teacher"
                    placeholder="Select Teacher..."
                    value={formData.homeroom_teacher_id || ""}
                    options={teachers}
                    onChange={(val) => setFormData({ ...formData, homeroom_teacher_id: val })}
                    getOptionLabel={(t) =>
                      `${t.last_name || ""}, ${t.first_name || ""}`.trim() || t.username
                    }
                    getOptionValue={(t) => t.id}
                  />
                </div>

                <div className="pt-3 flex items-center justify-end gap-2 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    {submitting ? (
                      <FiLoader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FiCheck className="w-3.5 h-3.5" />
                    )}
                    <span>
                      {editingItem
                        ? "Save Changes"
                        : "Create & Manage Assignments"}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* =========================================================================
          UPDATED DELETE CONFIRMATION MODAL
      ========================================================================= */}
      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-sm p-5 space-y-4 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-zinc-900">Delete Class Section</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Are you sure you want to delete{" "}
                  <span className="font-bold text-zinc-800">
                    {deletingItem?.name || deletingItem?.section_name || deletingItem?.display_name || "this item"}
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="w-full py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={submitting}
                  className="w-full py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                >
                  {submitting && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}